import BillingsCargoTaxiUpdateRequest, {
  BillingsCargoTaxiUpdateRequestBillingContribution,
  BillingsCargoTaxiUpdateRequestBillingContributionBaseAmount,
  BillingsCargoTaxiUpdateRequestBillingContributionChargeHaltings,
  BillingsCargoTaxiUpdateRequestBillingContributionContractCorrectionPercentage,
  BillingsCargoTaxiUpdateRequestBillingContributionDiscountBeingLate,
  BillingsCargoTaxiUpdateRequestBillingContributionDiscountIncompatibleCar,
  BillingsCargoTaxiUpdateRequestBillingContributionDiscountOptimization,
  BillingsCargoTaxiUpdateRequestBillingContributionDiscountOther,
  BillingsCargoTaxiUpdateRequestBillingContributionType,
  BillingsCargoTaxiUpdateRequestBillingNode,
} from "../../../../api/raily/billings/cargo-taxi/update/billings-cargo-taxi-update.request";
import BillingsCargoTaxiUpdateParams, {
  BillingsCargoTaxiUpdateParamsBillingContribution,
  BillingsCargoTaxiUpdateParamsBillingContributionBaseAmount,
  BillingsCargoTaxiUpdateParamsBillingContributionChargeHaltings,
  BillingsCargoTaxiUpdateParamsBillingContributionContractCorrectionPercentage,
  BillingsCargoTaxiUpdateParamsBillingContributionDiscountBeingLate,
  BillingsCargoTaxiUpdateParamsBillingContributionDiscountIncompatibleCar,
  BillingsCargoTaxiUpdateParamsBillingContributionDiscountOptimization,
  BillingsCargoTaxiUpdateParamsBillingContributionDiscountOther,
  BillingsCargoTaxiUpdateParamsBillingContributionType,
  BillingsCargoTaxiUpdateParamsBillingNode,
} from "./billings-cargo-taxi-update-params";

const createBillingNode = (
  node: BillingsCargoTaxiUpdateParamsBillingNode,
): BillingsCargoTaxiUpdateRequestBillingNode => {
  return {
    allowCharge: node.allowCharge,
    description: node.description,
    distance: node.distance,
    editableCoordinated: node.editableCoordinated,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    id: node.uuid,
    lat: node.lat,
    lon: node.lon,
    nodeId: node.nodeUuid,
    plannedDistance: node.plannedDistance,
    checkoutDate: node.checkoutDate,
    checkoutEventId: node.checkoutEventUuid,
    orderId: node.orderUuid,
    plannedDate: node.plannedDate,
  };
};

const createBillingNodes = (
  nodes: BillingsCargoTaxiUpdateParamsBillingNode[],
): BillingsCargoTaxiUpdateRequestBillingNode[] => {
  return nodes.map(createBillingNode);
};

const createBillingContributionBaseAmount = (
  contribution: BillingsCargoTaxiUpdateParamsBillingContributionBaseAmount,
): BillingsCargoTaxiUpdateRequestBillingContributionBaseAmount => {
  return {
    type: BillingsCargoTaxiUpdateRequestBillingContributionType.BASE_AMOUNT,
    comment: contribution.comment,
    distance: contribution.distance,
    rate: contribution.rate,
  };
};

const createBillingContributionChargeHaltings = (
  contribution: BillingsCargoTaxiUpdateParamsBillingContributionChargeHaltings,
): BillingsCargoTaxiUpdateRequestBillingContributionChargeHaltings => {
  return {
    type: BillingsCargoTaxiUpdateRequestBillingContributionType.CHARGE_HALTING,
    comment: contribution.comment,
    amount: contribution.amount,
    distance: contribution.distance,
  };
};

const createBillingContributionContractCorrectionPercentage = (
  contribution: BillingsCargoTaxiUpdateParamsBillingContributionContractCorrectionPercentage,
): BillingsCargoTaxiUpdateRequestBillingContributionContractCorrectionPercentage => {
  return {
    type: BillingsCargoTaxiUpdateRequestBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE,
    comment: contribution.comment,
    amount: contribution.amount,
    percentage: contribution.percentage,
  };
};

const createBillingContributionDiscountBeingLate = (
  contribution: BillingsCargoTaxiUpdateParamsBillingContributionDiscountBeingLate,
): BillingsCargoTaxiUpdateRequestBillingContributionDiscountBeingLate => {
  return {
    type: BillingsCargoTaxiUpdateRequestBillingContributionType.DISCOUNT_BEING_LATE,
    comment: contribution.comment,
    amount: contribution.amount,
  };
};

const createBillingContributionDiscountIncompatibleCar = (
  contribution: BillingsCargoTaxiUpdateParamsBillingContributionDiscountIncompatibleCar,
): BillingsCargoTaxiUpdateRequestBillingContributionDiscountIncompatibleCar => {
  return {
    type: BillingsCargoTaxiUpdateRequestBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR,
    comment: contribution.comment,
    amount: contribution.amount,
  };
};

const createBillingContributionDiscountOptimization = (
  contribution: BillingsCargoTaxiUpdateParamsBillingContributionDiscountOptimization,
): BillingsCargoTaxiUpdateRequestBillingContributionDiscountOptimization => {
  return {
    type: BillingsCargoTaxiUpdateRequestBillingContributionType.DISCOUNT_OPTIMIZATION,
    comment: contribution.comment,
    amount: contribution.amount,
  };
};

const createBillingContributionDiscountOther = (
  contribution: BillingsCargoTaxiUpdateParamsBillingContributionDiscountOther,
): BillingsCargoTaxiUpdateRequestBillingContributionDiscountOther => {
  return {
    type: BillingsCargoTaxiUpdateRequestBillingContributionType.DISCOUNT_OTHER,
    comment: contribution.comment,
    amount: contribution.amount,
  };
};

const createBillingContribution = (
  contribution: BillingsCargoTaxiUpdateParamsBillingContribution,
): BillingsCargoTaxiUpdateRequestBillingContribution => {
  switch (contribution.type) {
    case BillingsCargoTaxiUpdateParamsBillingContributionType.BASE_AMOUNT:
      return createBillingContributionBaseAmount(contribution);
    case BillingsCargoTaxiUpdateParamsBillingContributionType.CHARGE_HALTING:
      return createBillingContributionChargeHaltings(contribution);
    case BillingsCargoTaxiUpdateParamsBillingContributionType.CONTRACT_CORRECTION_PERCENTAGE:
      return createBillingContributionContractCorrectionPercentage(
        contribution,
      );
    case BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_BEING_LATE:
      return createBillingContributionDiscountBeingLate(contribution);
    case BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_INCOMPATIBLE_CAR:
      return createBillingContributionDiscountIncompatibleCar(contribution);
    case BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_OPTIMIZATION:
      return createBillingContributionDiscountOptimization(contribution);
    case BillingsCargoTaxiUpdateParamsBillingContributionType.DISCOUNT_OTHER:
      return createBillingContributionDiscountOther(contribution);
  }
};

const createBillingContributions = (
  contributions: BillingsCargoTaxiUpdateParamsBillingContribution[],
): BillingsCargoTaxiUpdateRequestBillingContribution[] => {
  return contributions.map(createBillingContribution);
};

const create = (
  params: BillingsCargoTaxiUpdateParams,
): BillingsCargoTaxiUpdateRequest => {
  return {
    billingUuid: params.billingUuid,
    billingNodes: createBillingNodes(params.billingNodes),
    billingContributions: createBillingContributions(
      params.billingContributions,
    ),
  };
};

const billingsCargoTaxiUpdateParamsFactory = {
  create,
};

export default billingsCargoTaxiUpdateParamsFactory;
